import { Trans, useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import { ContactInterestFlow } from '~/components/interests/ContactInterestFlow'
import { InterestDetailsView } from '~/components/interests/InterestDetailsView'
import { LoadingView } from '~/components/LoadingView'
import { Body } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { useContactInterest } from '~/data/interests/useContactInterest'
import { cn } from '~/lib/utils'

export function useInterestStatus(contactId: string) {
  const {
    data: interestStatus,
    isLoading: isLoadingInterest,
    refetch: refetchInterestStatus,
  } = useContactInterest(contactId)

  const displayInterest = interestStatus?.verifiedInterest ?? interestStatus?.realtorInterest
  const hasVerifiedInterest = !!interestStatus?.verifiedInterest

  const canEditInterest =
    !!interestStatus?.canEdit &&
    !!interestStatus?.realtorInterest &&
    !interestStatus?.verifiedInterest

  const canAddInterest =
    !!interestStatus?.canEdit &&
    !interestStatus?.realtorInterest &&
    !interestStatus?.verifiedInterest

  return {
    interestStatus,
    isLoadingInterest,
    refetchInterestStatus,
    displayInterest,
    hasVerifiedInterest,
    canEditInterest,
    canAddInterest,
  }
}

export function InterestFlowBottomSheet({
  isOpen,
  onOpenChange,
  contactId,
  assetId,
  allowNew = true,
  showInterestDetails = true,
}: {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  contactId: string
  assetId?: string
  allowNew?: boolean
  showInterestDetails?: boolean
}) {
  const { t } = useTranslations('interest')
  const { t: tFeedback } = useTranslations('feedback')

  const {
    interestStatus,
    isLoadingInterest,
    refetchInterestStatus,
    displayInterest,
    hasVerifiedInterest,
    canEditInterest,
    canAddInterest,
  } = useInterestStatus(contactId)

  return (
    <>
      <View className="mt-4 gap-4">
        {isLoadingInterest && <LoadingView />}
        {allowNew && canAddInterest && (
          <View className="mb-2 gap-2">
            <Heading3 className="text-center">
              {tFeedback(($) => $.requestFlow.addSearchTitle)}
            </Heading3>
            <Body className="text-pretty text-center text-sm/normal">
              <Trans
                t={tFeedback}
                i18nKey={($) => $.requestFlow.addSearchDescription}
                components={{ strong: <Text className="font-medium" /> }}
              />
            </Body>
          </View>
        )}
        {displayInterest && showInterestDetails && (
          <>
            <Text className="text-center font-medium text-xs uppercase text-secondary">
              {hasVerifiedInterest
                ? t(($) => $.types.verifiedInterest)
                : t(($) => $.types.preSavedInterest)}
            </Text>
            <InterestDetailsView
              interest={displayInterest.data}
              className={cn('rounded-xl border border-border bg-white')}
            />
          </>
        )}
        {(canEditInterest || canAddInterest) && (
          <Button
            testID="feedback-interest-button"
            variant="outline"
            size="lg"
            onPress={() => onOpenChange(true)}>
            {canAddInterest
              ? t(($) => $.actions.addContactInterest)
              : t(($) => $.actions.editInterest)}
          </Button>
        )}
      </View>

      <BottomSheet
        modalKey="contact-interest-flow"
        showCloseButton={false}
        showHandle={false}
        isOpen={isOpen}
        onOpenChange={onOpenChange}>
        <ContactInterestFlow
          key={`${contactId}-${assetId}-${interestStatus?.updatedAt?.toISOString() ?? 'new_interest'}`}
          assetId={assetId}
          contactId={contactId}
          onComplete={() => {
            void refetchInterestStatus()
            onOpenChange(false)
          }}
          onClose={() => onOpenChange(false)}
          isInBottomSheet
          className="max-h-[90vh] min-h-[350px] flex-1"
        />
      </BottomSheet>
    </>
  )
}
